import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, Heart, Mail, MessageSquare, Bell, Zap, Pencil, Trash2,
  Clock, Users, TrendingUp, Send, Eye, Pause, Play, X
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-600' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'paused', label: 'Paused', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-700' },
];
const CHANNELS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'push', label: 'Push', icon: Bell },
  { value: 'multi', label: 'Multi-Channel', icon: Zap },
];
const SEGMENTS = [
  { value: 'churned_30d', label: 'Churned 30 days' },
  { value: 'churned_60d', label: 'Churned 60 days' },
  { value: 'churned_90d', label: 'Churned 90+ days' },
  { value: 'inactive', label: 'Inactive users' },
  { value: 'downgraded', label: 'Downgraded plan' },
];
const OFFER_TYPES = [
  { value: 'discount', label: 'Discount' },
  { value: 'free_trial', label: 'Free Trial' },
  { value: 'bonus', label: 'Bonus Credit' },
  { value: 'upgrade', label: 'Free Upgrade' },
  { value: 'none', label: 'No Offer' },
];

function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }

function CampaignModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ name: '', description: '', channel: 'email', status: 'draft', target_segment: 'churned_30d', offer_type: 'discount', offer_value: '', message_template: '', target_count: 0 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ name: editing.name || '', description: editing.description || '', channel: editing.channel || 'email', status: editing.status || 'draft', target_segment: editing.target_segment || 'churned_30d', offer_type: editing.offer_type || 'discount', offer_value: editing.offer_value || '', message_template: editing.message_template || '', target_count: editing.target_count || 0 });
    else setForm({ name: '', description: '', channel: 'email', status: 'draft', target_segment: 'churned_30d', offer_type: 'discount', offer_value: '', message_template: '', target_count: 0 });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/retention/winback/${editing.id}`, form)
        : await axios.post(`${API}/api/retention/winback`, form);
      toast.success(editing ? 'Campaign updated' : 'Campaign created');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="flex items-center gap-2"><Heart className="h-5 w-5 text-rose-500" />{editing ? 'Edit' : 'New'} Win-Back Campaign</DialogTitle><DialogDescription>Re-engage churned customers</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Campaign Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Q1 Win-Back Blast" data-testid="campaign-name" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} data-testid="campaign-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Channel</Label><Select value={form.channel} onValueChange={v => setForm(p => ({...p, channel: v}))}><SelectTrigger data-testid="campaign-channel"><SelectValue /></SelectTrigger><SelectContent>{CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Status</Label><Select value={form.status} onValueChange={v => setForm(p => ({...p, status: v}))}><SelectTrigger data-testid="campaign-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Target Segment</Label><Select value={form.target_segment} onValueChange={v => setForm(p => ({...p, target_segment: v}))}><SelectTrigger data-testid="campaign-segment"><SelectValue /></SelectTrigger><SelectContent>{SEGMENTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Target Count</Label><Input type="number" value={form.target_count} onChange={e => setForm(p => ({...p, target_count: parseInt(e.target.value) || 0}))} data-testid="campaign-target-count" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Offer Type</Label><Select value={form.offer_type} onValueChange={v => setForm(p => ({...p, offer_type: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{OFFER_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Offer Value</Label><Input value={form.offer_value} onChange={e => setForm(p => ({...p, offer_value: e.target.value}))} placeholder="20% off" /></div>
          </div>
          <div><Label>Message Template</Label><Textarea value={form.message_template} onChange={e => setForm(p => ({...p, message_template: e.target.value}))} rows={3} placeholder="We miss you! Come back and enjoy..." data-testid="campaign-message" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="save-campaign-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create Campaign'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

function CampaignDetail({ campaign, onClose, onUpdate }) {
  if (!campaign) return null;
  const statusInfo = STATUSES.find(s => s.value === campaign.status) || STATUSES[0];
  const channelInfo = CHANNELS.find(c => c.value === campaign.channel) || CHANNELS[0];
  const ChannelIcon = channelInfo.icon;

  const updateStatus = async (newStatus) => {
    try {
      const { data } = await axios.put(`${API}/api/retention/winback/${campaign.id}`, { status: newStatus });
      onUpdate(data);
      toast.success(`Status: ${newStatus}`);
    } catch { toast.error('Failed'); }
  };

  const sentRate = campaign.target_count > 0 ? Math.round((campaign.sent_count / campaign.target_count) * 100) : 0;
  const openRate = campaign.sent_count > 0 ? Math.round((campaign.opened_count / campaign.sent_count) * 100) : 0;
  const convRate = campaign.sent_count > 0 ? Math.round((campaign.converted_count / campaign.sent_count) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="campaign-detail-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
          <div className="flex-1 min-w-0"><h2 className="text-base font-bold text-gray-900 line-clamp-1">{campaign.name}</h2><p className="text-xs text-gray-400">by {campaign.created_by_name}</p></div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8"><X className="h-4 w-4" /></Button>
        </div>
        <div className="px-5 py-3 border-b flex items-center gap-2 flex-wrap">
          <Badge className={`${statusInfo.color} text-xs`}>{statusInfo.label}</Badge>
          <Badge variant="outline" className="text-xs flex items-center gap-1"><ChannelIcon className="h-3 w-3" />{channelInfo.label}</Badge>
          <Badge variant="outline" className="text-xs">{SEGMENTS.find(s => s.value === campaign.target_segment)?.label || campaign.target_segment}</Badge>
        </div>
        {campaign.description && <div className="px-5 py-3 border-b"><p className="text-sm text-gray-600">{campaign.description}</p></div>}
        {(campaign.offer_type && campaign.offer_type !== 'none') && (
          <div className="px-5 py-3 border-b"><p className="text-xs text-gray-500"><span className="font-semibold">Offer:</span> {OFFER_TYPES.find(o => o.value === campaign.offer_type)?.label} — {campaign.offer_value}</p></div>
        )}
        <div className="px-5 py-3 border-b">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Performance</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Target', val: campaign.target_count, color: 'text-gray-700' },
              { label: 'Sent', val: `${campaign.sent_count} (${sentRate}%)`, color: 'text-blue-700' },
              { label: 'Opened', val: `${campaign.opened_count} (${openRate}%)`, color: 'text-amber-700' },
              { label: 'Converted', val: `${campaign.converted_count} (${convRate}%)`, color: 'text-green-700' },
            ].map((s, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-2 text-center"><p className={`text-sm font-bold ${s.color}`}>{s.val}</p><p className="text-[9px] text-gray-400">{s.label}</p></div>
            ))}
          </div>
        </div>
        {campaign.message_template && (
          <div className="px-5 py-3 border-b"><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Message Template</p><p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{campaign.message_template}</p></div>
        )}
        <div className="px-5 py-3 flex items-center gap-2 flex-wrap">
          {STATUSES.map(s => (
            <Button key={s.value} variant={campaign.status === s.value ? 'default' : 'outline'} size="sm" className={`h-7 text-[10px] ${campaign.status === s.value ? 'bg-rose-600' : ''}`} onClick={() => updateStatus(s.value)} data-testid={`set-campaign-status-${s.value}`}>{s.label}</Button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WinbackPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const { data } = await axios.get(`${API}/api/retention/winback`, { params,  });
      setCampaigns(data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filterStatus]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaved = (c, isUpdate) => { if (isUpdate) setCampaigns(p => p.map(x => x.id === c.id ? c : x)); else setCampaigns(p => [c, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete campaign?')) return; try { await axios.delete(`${API}/api/retention/winback/${id}`); setCampaigns(p => p.filter(c => c.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };
  const handleUpdate = (updated) => { setCampaigns(p => p.map(c => c.id === updated.id ? updated : c)); setSelectedCampaign(updated); };

  const activeCount = campaigns.filter(c => c.status === 'active').length;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="winback-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center"><Heart className="h-5 w-5 text-rose-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Win-Back Campaigns<PageInfoTooltip pageKey="winback" /></h1><p className="text-xs text-gray-400">{campaigns.length} campaigns · {activeCount} active</p></div></div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-28 h-9 text-sm"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-rose-600 hover:bg-rose-700 h-9 gap-1.5" data-testid="new-campaign-btn"><Plus className="h-4 w-4" /> New Campaign</Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-rose-500 border-t-transparent animate-spin" /></div> : campaigns.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><Heart className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No campaigns yet</h3><p className="text-sm text-gray-400 mb-4">Create win-back campaigns to re-engage churned customers</p><Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-rose-600 hover:bg-rose-700 gap-1.5"><Plus className="h-4 w-4" /> Create Campaign</Button></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {campaigns.map(c => {
            const statusInfo = STATUSES.find(s => s.value === c.status) || STATUSES[0];
            const channelInfo = CHANNELS.find(ch => ch.value === c.channel) || CHANNELS[0];
            const ChannelIcon = channelInfo.icon;
            const sentRate = c.target_count > 0 ? Math.round((c.sent_count / c.target_count) * 100) : 0;
            return (
              <Card key={c.id} className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedCampaign(c)} data-testid={`campaign-card-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 line-clamp-1">{c.name}</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Badge className={`${statusInfo.color} text-[9px]`}>{statusInfo.label}</Badge>
                        <Badge variant="outline" className="text-[9px] flex items-center gap-0.5"><ChannelIcon className="h-2.5 w-2.5" />{channelInfo.label}</Badge>
                      </div>
                    </div>
                  </div>
                  {c.description && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{c.description}</p>}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      { label: 'Target', val: c.target_count },
                      { label: 'Sent', val: c.sent_count },
                      { label: 'Opened', val: c.opened_count },
                      { label: 'Converted', val: c.converted_count },
                    ].map((s, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-1.5 text-center"><p className="text-sm font-bold text-gray-700">{s.val}</p><p className="text-[9px] text-gray-400">{s.label}</p></div>
                    ))}
                  </div>
                  {c.target_count > 0 && (
                    <div className="mb-3"><div className="flex items-center justify-between text-[10px] text-gray-400 mb-1"><span>Progress</span><span>{sentRate}%</span></div><div className="w-full bg-gray-200 rounded-full h-1.5"><div className="bg-rose-500 h-1.5 rounded-full transition-all" style={{ width: `${sentRate}%` }} /></div></div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{timeAgo(c.created_at)}</span>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(c); setModalOpen(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(c.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CampaignModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
      {selectedCampaign && <CampaignDetail campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} onUpdate={handleUpdate} />}
    </div>
  );
}
